"use client";

import { useMemo, useState, type ReactNode } from "react";
import {
  Check,
  ChevronDown,
  ChevronRight,
  Filter,
  Link2,
  LockKeyhole,
  Search,
  SlidersHorizontal,
  X,
} from "lucide-react";
import { Popover } from "@/components/ui/Popover";
import { Avatar } from "@/components/ui/Avatar";
import { PriorityIcon } from "@/components/ui/PriorityIcon";
import { StatusIcon } from "@/components/ui/StatusIcon";
import { PRIORITIES, STATUSES } from "@/lib/constants";
import {
  NO_FILTER_VALUE,
  clearTaskFilterFacet,
  countActiveTaskFilters,
  createEmptyTaskFilters,
  isTaskDateFilterActive,
  orderTaskFilterUsers,
  type TaskDateFilter,
  type TaskFilterFacet,
  type TaskFilters,
} from "@/lib/taskFilters";
import type { Cycle, Label, Project, Team, User } from "@/lib/types";
import { cn } from "@/lib/utils";

type FacetDefinition = {
  id: TaskFilterFacet;
  label: string;
  primary?: boolean;
};

const FACETS: FacetDefinition[] = [
  { id: "ownerIds", label: "Human owner", primary: true },
  { id: "executorAgentIds", label: "Agent", primary: true },
  { id: "requesterIds", label: "Reporter", primary: true },
  { id: "projectIds", label: "Project", primary: true },
  { id: "created", label: "Created", primary: true },
  { id: "teamIds", label: "Team" },
  { id: "statuses", label: "Status" },
  { id: "priorities", label: "Priority" },
  { id: "labelIds", label: "Labels" },
  { id: "due", label: "Due date" },
  { id: "updated", label: "Updated" },
  { id: "completed", label: "Completed" },
  { id: "authorIds", label: "Created by" },
  { id: "cycleIds", label: "Cycle" },
  { id: "estimates", label: "Estimate" },
  { id: "hierarchy", label: "Parent / subtask" },
  { id: "visibility", label: "Visibility" },
];

type FilterData = {
  users: User[];
  projects: Project[];
  teams: Team[];
  labels: Label[];
  cycles: Cycle[];
  estimates: number[];
};

type Choice = {
  id: string;
  label: string;
  subtitle?: string;
  search?: string;
  group?: string;
  icon?: ReactNode;
  muted?: boolean;
};

function activeFacetValueCount(filters: TaskFilters, facet: TaskFilterFacet): number {
  const value = filters[facet];
  if (Array.isArray(value)) return value.length;
  return isTaskDateFilterActive(value) ? 1 : 0;
}

function personChoices(users: User[], emptyLabel: string): Choice[] {
  return [
    {
      id: NO_FILTER_VALUE,
      label: emptyLabel,
      muted: true,
      icon: <Avatar user={null} size={18} />,
    },
    ...orderTaskFilterUsers(users).map((user) => ({
      id: user.id,
      label: user.name,
      subtitle: user.email,
      search: `${user.name} ${user.email}`,
      group: user.isBot ? "Agents" : "People",
      icon: <Avatar user={user} size={18} />,
    })),
  ];
}

function toggleValue(values: string[], value: string): string[] {
  return values.includes(value) ? values.filter((candidate) => candidate !== value) : [...values, value];
}

function FilterChoicePanel({
  choices,
  selected,
  onChange,
  placeholder,
}: {
  choices: Choice[];
  selected: string[];
  onChange: (values: string[]) => void;
  placeholder: string;
}) {
  const [query, setQuery] = useState("");
  const visible = choices.filter((choice) =>
    `${choice.label} ${choice.search ?? ""}`.toLowerCase().includes(query.trim().toLowerCase())
  );
  let previousGroup: string | undefined;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-2 border-b border-[var(--border)] px-3 py-2">
        <Search size={12} className="text-[var(--fg-tertiary)]" />
        <input
          autoFocus
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={placeholder}
          className="min-w-0 flex-1 bg-transparent text-[12.5px] outline-none placeholder:text-[var(--fg-muted)]"
        />
        {selected.length > 0 && (
          <button
            type="button"
            onClick={() => onChange([])}
            className="rounded px-1.5 py-1 text-[11px] font-medium text-[var(--fg-tertiary)] hover:bg-[var(--bg-hover)] hover:text-[var(--fg)] transition-colors"
          >
            Clear
          </button>
        )}
      </div>
      <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto py-1">
        {visible.map((choice) => {
          const showGroup = !!choice.group && choice.group !== previousGroup;
          previousGroup = choice.group;
          return (
            <div key={choice.id}>
              {showGroup && (
                <div className="px-3 pb-1 pt-2 text-[10.5px] font-medium uppercase tracking-wider text-[var(--fg-muted)]">
                  {choice.group}
                </div>
              )}
              <button
                type="button"
                onClick={() => onChange(toggleValue(selected, choice.id))}
                aria-pressed={selected.includes(choice.id)}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[12.5px] hover:bg-[var(--bg-hover)] transition-colors"
              >
                <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center">
                  {choice.icon}
                </span>
                <span className="min-w-0 flex-1">
                  <span
                    className={cn(
                      "block truncate",
                      choice.muted && "text-[var(--fg-secondary)]"
                    )}
                  >
                    {choice.label}
                  </span>
                  {choice.subtitle && (
                    <span className="block truncate text-[10.5px] text-[var(--fg-muted)]">
                      {choice.subtitle}
                    </span>
                  )}
                </span>
                {selected.includes(choice.id) && <Check size={13} className="text-[var(--accent)]" />}
              </button>
            </div>
          );
        })}
        {visible.length === 0 && (
          <div className="px-3 py-8 text-center text-[12px] text-[var(--fg-muted)]">No matches</div>
        )}
      </div>
    </div>
  );
}

function DateFilterPanel({
  value,
  onChange,
  allowEmpty,
  emptyLabel,
}: {
  value: TaskDateFilter;
  onChange: (value: TaskDateFilter) => void;
  allowEmpty?: boolean;
  emptyLabel?: string;
}) {
  function changeFrom(from: string | null) {
    onChange({
      ...value,
      from,
      to: from && value.to && from > value.to ? from : value.to,
    });
  }

  function changeTo(to: string | null) {
    onChange({
      ...value,
      from: to && value.from && to < value.from ? to : value.from,
      to,
    });
  }

  return (
    <div className="space-y-4 p-4">
      <div>
        <div className="mb-2 text-[11px] font-medium uppercase tracking-wider text-[var(--fg-muted)]">
          Date range
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="space-y-1">
            <span className="text-[11.5px] text-[var(--fg-tertiary)]">From</span>
            <input
              type="date"
              value={value.from ?? ""}
              max={value.to ?? undefined}
              onChange={(event) => changeFrom(event.target.value || null)}
              className="w-full rounded-md border border-[var(--border)] bg-[var(--bg-tertiary)] px-3 py-1.5 text-[12.5px] outline-none focus:border-[var(--accent)]"
            />
          </label>
          <label className="space-y-1">
            <span className="text-[11.5px] text-[var(--fg-tertiary)]">To</span>
            <input
              type="date"
              value={value.to ?? ""}
              min={value.from ?? undefined}
              onChange={(event) => changeTo(event.target.value || null)}
              className="w-full rounded-md border border-[var(--border)] bg-[var(--bg-tertiary)] px-3 py-1.5 text-[12.5px] outline-none focus:border-[var(--accent)]"
            />
          </label>
        </div>
        <p className="mt-2 text-[11.5px] text-[var(--fg-muted)]">Both dates are inclusive.</p>
      </div>
      {allowEmpty && (
        <button
          type="button"
          onClick={() => onChange({ ...value, includeEmpty: !value.includeEmpty })}
          aria-pressed={value.includeEmpty}
          className="flex w-full items-center gap-2 rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] px-3 py-2 text-left text-[12.5px] hover:bg-[var(--bg-hover)] transition-colors"
        >
          <span
            className={cn(
              "flex h-4 w-4 items-center justify-center rounded border",
              value.includeEmpty
                ? "border-[var(--accent)] bg-[var(--accent)] text-white"
                : "border-[var(--border-strong)]"
            )}
          >
            {value.includeEmpty && <Check size={11} />}
          </span>
          {emptyLabel}
        </button>
      )}
      {isTaskDateFilterActive(value) && (
        <button
          type="button"
          onClick={() => onChange({ from: null, to: null, includeEmpty: false })}
          className="text-[12px] font-medium text-[var(--fg-tertiary)] hover:text-[var(--fg)] transition-colors"
        >
          Clear date filter
        </button>
      )}
    </div>
  );
}

function FilterFacetContent({
  facet,
  filters,
  onChange,
  data,
}: {
  facet: TaskFilterFacet;
  filters: TaskFilters;
  onChange: (filters: TaskFilters) => void;
  data: FilterData;
}) {
  if (
    facet === "assigneeIds" ||
    facet === "ownerIds" ||
    facet === "executorAgentIds" ||
    facet === "requesterIds" ||
    facet === "authorIds"
  ) {
    const emptyLabel =
      facet === "ownerIds"
        ? "No owner"
        : facet === "executorAgentIds"
          ? "No agent"
          : facet === "assigneeIds"
            ? "Unassigned"
            : facet === "requesterIds"
              ? "No reporter"
              : "No creator";
    const placeholder =
      facet === "ownerIds"
        ? "Search people…"
        : facet === "executorAgentIds"
          ? "Search agents…"
          : facet === "assigneeIds"
            ? "Search assignees…"
            : "Search people or agents…";
    const people =
      facet === "ownerIds"
        ? data.users.filter((user) => !user.isBot)
        : facet === "executorAgentIds"
          ? data.users.filter((user) => user.isBot)
          : data.users;
    return (
      <FilterChoicePanel
        choices={personChoices(people, emptyLabel)}
        selected={filters[facet]}
        onChange={(values) => onChange({ ...filters, [facet]: values })}
        placeholder={placeholder}
      />
    );
  }

  if (facet === "projectIds") {
    return (
      <FilterChoicePanel
        choices={[
          { id: NO_FILTER_VALUE, label: "No project", muted: true },
          ...data.projects
            .slice()
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((project) => ({ id: project.id, label: project.name, icon: <span>{project.icon}</span> })),
        ]}
        selected={filters.projectIds}
        onChange={(values) => onChange({ ...filters, projectIds: values })}
        placeholder="Search projects…"
      />
    );
  }

  if (facet === "teamIds") {
    return (
      <FilterChoicePanel
        choices={data.teams
          .slice()
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((team) => ({ id: team.id, label: team.name, icon: <span>{team.icon}</span> }))}
        selected={filters.teamIds}
        onChange={(values) => onChange({ ...filters, teamIds: values })}
        placeholder="Search teams…"
      />
    );
  }

  if (facet === "statuses") {
    return (
      <FilterChoicePanel
        choices={STATUSES.map((status) => ({
          id: status.id,
          label: status.name,
          icon: <StatusIcon status={status.id} size={13} />,
        }))}
        selected={filters.statuses}
        onChange={(values) => onChange({ ...filters, statuses: values as TaskFilters["statuses"] })}
        placeholder="Search statuses…"
      />
    );
  }

  if (facet === "priorities") {
    return (
      <FilterChoicePanel
        choices={PRIORITIES.map((priority) => ({
          id: String(priority.id),
          label: priority.name,
          icon: <PriorityIcon priority={priority.id} size={13} />,
        }))}
        selected={filters.priorities.map(String)}
        onChange={(values) =>
          onChange({ ...filters, priorities: values.map(Number) as TaskFilters["priorities"] })
        }
        placeholder="Search priorities…"
      />
    );
  }

  if (facet === "labelIds") {
    const teamNames = new Map(data.teams.map((team) => [team.id, team.name]));
    return (
      <FilterChoicePanel
        choices={[
          { id: NO_FILTER_VALUE, label: "No labels", muted: true },
          ...data.labels
            .slice()
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((label) => ({
              id: label.id,
              label: label.name,
              group: teamNames.get(label.teamId),
              icon: <span className="h-2.5 w-2.5 rounded-full" style={{ background: label.color }} />,
            })),
        ]}
        selected={filters.labelIds}
        onChange={(values) => onChange({ ...filters, labelIds: values })}
        placeholder="Search labels…"
      />
    );
  }

  if (facet === "cycleIds") {
    const teamNames = new Map(data.teams.map((team) => [team.id, team.name]));
    return (
      <FilterChoicePanel
        choices={[
          { id: NO_FILTER_VALUE, label: "No cycle", muted: true },
          ...data.cycles
            .slice()
            .sort((a, b) => b.startDate - a.startDate)
            .map((cycle) => ({
              id: cycle.id,
              label: cycle.name,
              group: teamNames.get(cycle.teamId),
            })),
        ]}
        selected={filters.cycleIds}
        onChange={(values) => onChange({ ...filters, cycleIds: values })}
        placeholder="Search cycles…"
      />
    );
  }

  if (facet === "estimates") {
    return (
      <FilterChoicePanel
        choices={[
          { id: NO_FILTER_VALUE, label: "No estimate", muted: true },
          ...data.estimates.map((estimate) => ({ id: String(estimate), label: `${estimate} points` })),
        ]}
        selected={filters.estimates}
        onChange={(values) => onChange({ ...filters, estimates: values })}
        placeholder="Search estimates…"
      />
    );
  }

  if (facet === "hierarchy") {
    return (
      <FilterChoicePanel
        choices={[
          { id: "top_level", label: "Top-level tasks" },
          { id: "subtask", label: "Subtasks" },
        ]}
        selected={filters.hierarchy}
        onChange={(values) =>
          onChange({ ...filters, hierarchy: values as TaskFilters["hierarchy"] })
        }
        placeholder="Search task type…"
      />
    );
  }

  if (facet === "visibility") {
    return (
      <FilterChoicePanel
        choices={[
          { id: "private", label: "Workspace only" },
          { id: "public", label: "Public" },
        ]}
        selected={filters.visibility}
        onChange={(values) =>
          onChange({ ...filters, visibility: values as TaskFilters["visibility"] })
        }
        placeholder="Search visibility…"
      />
    );
  }

  const dateValue = filters[facet] as TaskDateFilter;
  return (
    <DateFilterPanel
      value={dateValue}
      onChange={(value) => onChange({ ...filters, [facet]: value })}
      allowEmpty={facet === "due" || facet === "completed"}
      emptyLabel={facet === "due" ? "Include tasks with no due date" : "Include unfinished tasks"}
    />
  );
}

export function TaskFilterButton({
  filters,
  onChange,
  data,
}: {
  filters: TaskFilters;
  onChange: (filters: TaskFilters) => void;
  data: FilterData;
}) {
  const [activeFacet, setActiveFacet] = useState<TaskFilterFacet>("ownerIds");
  const [showMore, setShowMore] = useState(false);
  const activeCount = countActiveTaskFilters(filters);
  const secondaryFacets = FACETS.filter((facet) => !facet.primary);
  const activeSecondaryCount = secondaryFacets.filter(
    (facet) => activeFacetValueCount(filters, facet.id) > 0
  ).length;

  return (
    <Popover
      align="end"
      width={580}
      className="overflow-hidden"
      trigger={
        <button
          type="button"
          aria-label={activeCount > 0 ? `Filter tasks, ${activeCount} active` : "Filter tasks"}
          aria-haspopup="dialog"
          className={cn(
            "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[12px] font-medium transition-colors",
            activeCount > 0
              ? "border-[var(--accent)] bg-[var(--accent-bg)] text-[var(--fg)] hover:bg-[var(--bg-hover)]"
              : "border-[var(--border)] bg-[var(--bg-secondary)] text-[var(--fg-secondary)] hover:bg-[var(--bg-hover)]"
          )}
        >
          <Filter size={12} />
          <span className="hidden lg:inline">Filter</span>
          {activeCount > 0 && (
            <span className="rounded bg-[var(--bg-active)] px-1.5 py-0.5 text-[10.5px] text-[var(--fg)]">
              {activeCount}
            </span>
          )}
        </button>
      }
    >
      {() => (
        <div
          role="dialog"
          aria-label="Filter tasks"
          className="flex h-[min(420px,calc(100vh-24px))] min-h-0 max-sm:flex-col"
        >
          <div className="w-[176px] shrink-0 border-r border-[var(--border)] bg-[var(--bg-secondary)] py-2 max-sm:w-full max-sm:border-b max-sm:border-r-0 max-sm:py-1">
            <div className="px-3 pb-1.5 text-[10.5px] font-medium uppercase tracking-wider text-[var(--fg-muted)] max-sm:hidden">
              Filter tasks
            </div>
            <div className="max-sm:scrollbar-thin max-sm:flex max-sm:overflow-x-auto">
              {FACETS.filter((facet) => facet.primary).map((facet) => (
                <FacetButton
                  key={facet.id}
                  facet={facet}
                  active={activeFacet === facet.id}
                  count={activeFacetValueCount(filters, facet.id)}
                  onClick={() => setActiveFacet(facet.id)}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={() => setShowMore((current) => !current)}
              aria-expanded={showMore}
              aria-controls="task-filter-secondary-facets"
              className="mt-2 flex w-full items-center gap-2 border-t border-[var(--border)] px-3 pb-1.5 pt-2 text-left text-[10.5px] font-medium uppercase tracking-wider text-[var(--fg-muted)] hover:bg-[var(--bg-hover)] transition-colors max-sm:mt-0 max-sm:border-t-0"
            >
              {showMore ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
              <span className="flex-1">More filters</span>
              {activeSecondaryCount > 0 && (
                <span className="rounded bg-[var(--accent-bg)] px-1.5 py-0.5 text-[10.5px] text-[var(--accent)]">
                  {activeSecondaryCount}
                </span>
              )}
            </button>
            {showMore && (
              <div
                id="task-filter-secondary-facets"
                className="scrollbar-thin max-h-[248px] overflow-y-auto max-sm:flex max-sm:max-h-none max-sm:overflow-x-auto max-sm:overflow-y-hidden"
              >
                {secondaryFacets.map((facet) => (
                  <FacetButton
                    key={facet.id}
                    facet={facet}
                    active={activeFacet === facet.id}
                    count={activeFacetValueCount(filters, facet.id)}
                    onClick={() => setActiveFacet(facet.id)}
                  />
                ))}
              </div>
            )}
          </div>
          <div className="flex min-w-0 flex-1 flex-col">
            <div className="flex h-10 shrink-0 items-center border-b border-[var(--border)] px-3">
              <div className="text-[12.5px] font-medium">
                {FACETS.find((facet) => facet.id === activeFacet)?.label}
              </div>
              {activeCount > 0 && (
                <button
                  type="button"
                  onClick={() => onChange(createEmptyTaskFilters())}
                  className="ml-auto rounded px-1.5 py-1 text-[11px] font-medium text-[var(--fg-tertiary)] hover:bg-[var(--bg-hover)] hover:text-[var(--fg)] transition-colors"
                >
                  Clear all
                </button>
              )}
            </div>
            <div className="min-h-0 flex-1">
              <FilterFacetContent
                key={activeFacet}
                facet={activeFacet}
                filters={filters}
                onChange={onChange}
                data={data}
              />
            </div>
            <div className="shrink-0 border-t border-[var(--border)] px-3 py-2 text-[10.5px] text-[var(--fg-muted)]">
              Across filters = AND · within one filter = OR
            </div>
          </div>
        </div>
      )}
    </Popover>
  );
}

function FacetButton({
  facet,
  active,
  count,
  onClick,
}: {
  facet: FacetDefinition;
  active: boolean;
  count: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "flex w-full items-center gap-2 px-3 py-1.5 text-left text-[12px] transition-colors max-sm:w-auto max-sm:shrink-0",
        active
          ? "bg-[var(--bg-active)] text-[var(--fg)]"
          : "text-[var(--fg-secondary)] hover:bg-[var(--bg-hover)]"
      )}
    >
      <SlidersHorizontal size={11} className="text-[var(--fg-tertiary)]" />
      <span className="min-w-0 flex-1 truncate">{facet.label}</span>
      {count > 0 && (
        <span className="rounded bg-[var(--accent-bg)] px-1.5 py-0.5 text-[10.5px] text-[var(--accent)]">
          {count}
        </span>
      )}
    </button>
  );
}

function formatDateValue(value: string): string {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: new Date().getFullYear() === year ? undefined : "numeric",
  });
}

function summarizeDate(value: TaskDateFilter, emptyLabel: string): string {
  const range =
    value.from && value.to
      ? `${formatDateValue(value.from)} – ${formatDateValue(value.to)}`
      : value.from
        ? `from ${formatDateValue(value.from)}`
        : value.to
          ? `through ${formatDateValue(value.to)}`
          : "";
  if (range && value.includeEmpty) return `${range} or ${emptyLabel}`;
  return range || emptyLabel;
}

function summarizeValues(values: string[], names: Map<string, string>, emptyLabel: string): string {
  const labels = values.map((value) => (value === NO_FILTER_VALUE ? emptyLabel : names.get(value) ?? value));
  if (labels.length <= 2) return labels.join(", ");
  return `${labels.slice(0, 2).join(", ")} +${labels.length - 2}`;
}

function filterSummaries(filters: TaskFilters, data: FilterData) {
  const userNames = new Map(data.users.map((user) => [user.id, user.name]));
  const projectNames = new Map(data.projects.map((project) => [project.id, project.name]));
  const teamNames = new Map(data.teams.map((team) => [team.id, team.name]));
  const labelNames = new Map(data.labels.map((label) => [label.id, label.name]));
  const cycleNames = new Map(data.cycles.map((cycle) => [cycle.id, cycle.name]));
  const statusNames = new Map(STATUSES.map((status) => [status.id, status.name]));
  const priorityNames = new Map(PRIORITIES.map((priority) => [String(priority.id), priority.name]));
  const summaries: { facet: TaskFilterFacet; label: string; value: string }[] = [];
  const add = (facet: TaskFilterFacet, label: string, value: string) => summaries.push({ facet, label, value });

  if (filters.assigneeIds.length) {
    add(
      "assigneeIds",
      "Legacy assignment",
      summarizeValues(filters.assigneeIds, userNames, "Unassigned")
    );
  }
  if (filters.ownerIds.length) add("ownerIds", "Owner", summarizeValues(filters.ownerIds, userNames, "No owner"));
  if (filters.executorAgentIds.length) add("executorAgentIds", "Agent", summarizeValues(filters.executorAgentIds, userNames, "No agent"));
  if (filters.requesterIds.length) add("requesterIds", "Reporter", summarizeValues(filters.requesterIds, userNames, "No reporter"));
  if (filters.projectIds.length) add("projectIds", "Project", summarizeValues(filters.projectIds, projectNames, "No project"));
  if (isTaskDateFilterActive(filters.created)) add("created", "Created", summarizeDate(filters.created, "any date"));
  if (filters.teamIds.length) add("teamIds", "Team", summarizeValues(filters.teamIds, teamNames, "No team"));
  if (filters.statuses.length) add("statuses", "Status", summarizeValues(filters.statuses, statusNames, "No status"));
  if (filters.priorities.length) add("priorities", "Priority", summarizeValues(filters.priorities.map(String), priorityNames, "No priority"));
  if (filters.labelIds.length) add("labelIds", "Labels", summarizeValues(filters.labelIds, labelNames, "No labels"));
  if (isTaskDateFilterActive(filters.due)) add("due", "Due", summarizeDate(filters.due, "no due date"));
  if (isTaskDateFilterActive(filters.updated)) add("updated", "Updated", summarizeDate(filters.updated, "any date"));
  if (isTaskDateFilterActive(filters.completed)) add("completed", "Completed", summarizeDate(filters.completed, "unfinished"));
  if (filters.authorIds.length) add("authorIds", "Created by", summarizeValues(filters.authorIds, userNames, "No creator"));
  if (filters.cycleIds.length) add("cycleIds", "Cycle", summarizeValues(filters.cycleIds, cycleNames, "No cycle"));
  if (filters.estimates.length) add("estimates", "Estimate", summarizeValues(filters.estimates, new Map(data.estimates.map((value) => [String(value), `${value} pts`])), "No estimate"));
  if (filters.hierarchy.length) add("hierarchy", "Type", summarizeValues(filters.hierarchy, new Map([["top_level", "Top-level"], ["subtask", "Subtask"]]), "Any"));
  if (filters.visibility.length) add("visibility", "Visibility", summarizeValues(filters.visibility, new Map([["private", "Workspace only"], ["public", "Public"]]), "Any"));
  return summaries;
}

export function TaskFilterSummaryBar({
  filters,
  onChange,
  searchValue,
  onSearchChange,
  data,
  inheritedScopes,
  resultCount,
  totalCount,
}: {
  filters: TaskFilters;
  onChange: (filters: TaskFilters) => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  data: FilterData;
  inheritedScopes: string[];
  resultCount: number;
  totalCount: number;
}) {
  const summaries = useMemo(() => filterSummaries(filters, data), [data, filters]);
  const activeSearch = searchValue.trim();
  if (summaries.length === 0 && inheritedScopes.length === 0 && !activeSearch) return null;

  return (
    <div className="scrollbar-thin flex shrink-0 items-center gap-1.5 overflow-x-auto border-b border-[var(--border)] bg-[var(--bg-secondary)] px-3 py-2 md:px-5">
      {inheritedScopes.map((scope) => (
        <span
          key={scope}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-[var(--border)] bg-[var(--bg-tertiary)] px-2 py-1 text-[11.5px] text-[var(--fg-tertiary)]"
          title="This scope comes from the current view"
        >
          <LockKeyhole size={10} />
          {scope}
        </span>
      ))}
      {summaries.map((summary) => (
        <span
          key={summary.facet}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-[var(--border)] bg-[var(--bg-tertiary)] px-2 py-1 text-[11.5px] text-[var(--fg-secondary)]"
        >
          <span className="text-[var(--fg-muted)]">{summary.label}:</span>
          <span className="max-w-[220px] truncate">{summary.value}</span>
          <button
            type="button"
            onClick={() => onChange(clearTaskFilterFacet(filters, summary.facet))}
            className="-mr-1 flex h-4 w-4 items-center justify-center rounded text-[var(--fg-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--fg)] transition-colors"
            aria-label={`Remove ${summary.label} filter`}
          >
            <X size={10} />
          </button>
        </span>
      ))}
      {activeSearch && (
        <span className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-[var(--border)] bg-[var(--bg-tertiary)] px-2 py-1 text-[11.5px] text-[var(--fg-secondary)]">
          <Search size={10} className="text-[var(--fg-muted)]" />
          <span className="text-[var(--fg-muted)]">Search:</span>
          <span className="max-w-[220px] truncate">{activeSearch}</span>
          <button
            type="button"
            onClick={() => onSearchChange("")}
            className="-mr-1 flex h-4 w-4 items-center justify-center rounded text-[var(--fg-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--fg)] transition-colors"
            aria-label="Clear task search"
          >
            <X size={10} />
          </button>
        </span>
      )}
      <span className="ml-auto shrink-0 text-[11.5px] text-[var(--fg-muted)]">
        {resultCount} of {totalCount} {totalCount === 1 ? "task" : "tasks"}
      </span>
      {summaries.length > 0 && (
        <button
          type="button"
          onClick={() => onChange(createEmptyTaskFilters())}
          className="shrink-0 rounded px-1.5 py-1 text-[11.5px] font-medium text-[var(--fg-tertiary)] hover:bg-[var(--bg-hover)] hover:text-[var(--fg)] transition-colors"
        >
          Clear all
        </button>
      )}
    </div>
  );
}

export function CopyTaskViewButton({ getUrl }: { getUrl: () => string }) {
  const [copied, setCopied] = useState(false);

  async function copyView() {
    try {
      await navigator.clipboard.writeText(getUrl());
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  }

  return (
    <button
      type="button"
      onClick={() => void copyView()}
      aria-label={copied ? "Task view URL copied" : "Copy exact task view URL"}
      className="inline-flex items-center gap-1.5 rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] px-2.5 py-1 text-[12px] font-medium text-[var(--fg-secondary)] hover:bg-[var(--bg-hover)] transition-colors"
      title="Copy this exact filtered view. Workspace sign-in is required."
    >
      {copied ? <Check size={12} /> : <Link2 size={12} />}
      <span className="hidden xl:inline">{copied ? "Copied" : "Copy view"}</span>
    </button>
  );
}
